import { Component, Inject, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Level } from 'src/app/models/slat/slat-levels-dto'
import { SlatAssessmentItemDto } from '../../../../models/slat/slat-assessment-item-dto'
import { CommonService } from '../../../../services/common/common.service'

@Component({
  selector: 'app-slat-edit-modal',
  templateUrl: './slat-edit-modal.component.html',
  styleUrl: './slat-edit-modal.component.scss',
  standalone: false,
})
export class SlatEditModalComponent implements OnInit {
  isCoreLanguage = false
  rawScoreLabel = ''
  scoreLabel = ''
  form: FormGroup
  levels: Level[] = []
  templateId = ''
  groupId = ''
  itemId = ''
  name = ''
  description = ''
  groupName = ''
  items: SlatAssessmentItemDto[] = []
  currentIndex: number = 0

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<SlatEditModalComponent>,
    private commonService: CommonService,
  ) {
    this.isCoreLanguage =
      this.data?.items?.[this.data.currentIndex]?.isCore ?? false
    this.initializeForm()
    this.applyValidatorsForMode() // ensure validators reflect initial mode
  }

  ngOnInit(): void {
    if (this.data?.items && typeof this.data.currentIndex === 'number') {
      const {
        rawScore,
        score,
        percentileRank,
        level,
        levels,
        templateId,
        groupId,
        itemId,
        name,
        description,
        groupName,
      } = this.data.items[this.data.currentIndex]
      this.form.patchValue({
        rawScore: rawScore ?? null,
        scaledScore: score ?? null,
        percentileRank: percentileRank ?? null,
        level:
          levels.filter((lvl: Level) => lvl.levelId == level)[0]?.levelId ?? '',
      })

      this.levels = levels ?? []
      this.templateId = templateId ?? ''
      this.groupId = groupId ?? ''
      this.itemId = itemId ?? ''
      this.name = this.commonService.translateSafe(name) ?? ''
      this.description = this.commonService.translateSafe(description) ?? ''
      this.groupName = this.commonService.translateSafe(groupName) ?? ''
      this.items = this.data.items ?? []
      this.currentIndex = this.data.currentIndex ?? 0
      this.loadItem(this.items[this.currentIndex])
    }
  }

  onSubmit(): void {
    this.applyValidatorsForMode()
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saveCurrentItem()
    this.dialogRef.close({ updatedItems: this.items })
  }

  onCancel(): void {
    this.dialogRef.close()
  }

  onNext(): void {
    // Ensure rules match current item before checking validity
    this.applyValidatorsForMode()
    if (this.form.invalid) {
      return
    }
    this.saveCurrentItem()
    if (this.currentIndex < this.items.length - 1) {
      this.currentIndex++
      this.loadItem(this.items[this.currentIndex])
    }
    Object.values(this.form.controls).forEach((ctrl) => {
      ctrl.markAsPristine()
      ctrl.markAsUntouched()
    })
  }

  onPrevious(): void {
    this.applyValidatorsForMode()
    if (this.form.invalid) {
      return
    }
    this.saveCurrentItem()
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.loadItem(this.items[this.currentIndex])
    }
    Object.values(this.form.controls).forEach((ctrl) => {
      ctrl.markAsPristine()
      ctrl.markAsUntouched()
    })
  }

  private saveCurrentItem() {
    const updatedItem = this.updateData()
    this.items[this.currentIndex] = {
      ...this.items[this.currentIndex],
      ...updatedItem,
    }
  }

  private updateData(): any {
    return {
      rawScore: this.isCoreLanguage
        ? null
        : Number(this.form.controls['rawScore']?.value),
      score: Number(this.form.controls['scaledScore']?.value),
      percentileRank: this.form.controls['percentileRank']?.value,
      level:
        this.form.controls['level']?.value === ''
          ? ''
          : this.levels.find(
              (item) => item.levelId === this.form.controls['level']?.value,
            ).levelId ?? '',
      levelItem:
        this.form.controls['level']?.value === ''
          ? ''
          : this.levels.find(
              (item) => item.levelId === this.form.controls['level']?.value,
            ) ?? '',
      templateId: this.templateId,
      groupId: this.groupId,
      itemId: this.itemId,
    }
  }

  private initializeForm(): void {
    // Create all controls once; do not hard-code "required" here
    this.form = new FormGroup({
      rawScore: new FormControl('', [
        Validators.pattern('^[0-9]*$'),
        Validators.min(0),
        Validators.max(100),
      ]),
      scaledScore: new FormControl('', [
        Validators.pattern('^[0-9]*$'),
        Validators.min(1),
        Validators.max(160),
      ]),
      percentileRank: new FormControl('', [
        Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$'),
        Validators.min(0),
        Validators.max(100),
      ]),
      level: new FormControl('', []),
    })
  }

  private applyValidatorsForMode(): void {
    const rawCtrl = this.form.get('rawScore') as FormControl
    const scaledCtrl = this.form.get('scaledScore') as FormControl
    const prCtrl = this.form.get('percentileRank') as FormControl
    const levelCtrl = this.form.get('level') as FormControl

    // Base validators (no "required")
    const rawBase = [
      Validators.pattern('^[0-9]*$'),
      Validators.min(0),
      Validators.max(100),
    ]
    const scaledBase = [
      Validators.pattern('^[0-9]*$'),
      Validators.min(1),
      Validators.max(160),
    ]
    const prBase = [
      Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$'),
      Validators.min(0),
      Validators.max(100),
    ]

    if (this.isCoreLanguage) {
      // Core Language: scaledScore, percentileRank, and level are required; rawScore is optional
      rawCtrl.setValidators(rawBase)
      scaledCtrl.setValidators([...scaledBase, Validators.required])
      prCtrl.setValidators([...prBase, Validators.required])
      levelCtrl.setValidators([Validators.required])
    } else {
      // Sub-Test: rawScore is required; scaledScore, percentileRank, and level are optional
      rawCtrl.setValidators([...rawBase, Validators.required])
      scaledCtrl.setValidators(scaledBase)
      prCtrl.setValidators(prBase)
      levelCtrl.setValidators([])
    }

    rawCtrl.updateValueAndValidity({ onlySelf: true })
    scaledCtrl.updateValueAndValidity({ onlySelf: true })
    prCtrl.updateValueAndValidity({ onlySelf: true })
    levelCtrl.updateValueAndValidity({ onlySelf: true })

    // Recompute the form's aggregated validity after toggling rules
    this.form.updateValueAndValidity()
  }

  private loadItem(item: SlatAssessmentItemDto): void {
    const {
      rawScore,
      score,
      percentileRank,
      level,
      levels,
      templateId,
      groupId,
      itemId,
      name,
      description,
      groupName,
      isCore,
    } = item

    // Set current mode and re-apply validators before patching (order not critical, but consistent)
    this.isCoreLanguage = isCore ?? false
    this.applyValidatorsForMode()

    if (isCore) {
      this.form.patchValue({
        rawScore: null,
        scaledScore: score ?? null,
        percentileRank: percentileRank ?? null,
        level: levels?.find((lvl) => lvl.levelId === level)?.levelId ?? '',
      })
    } else {
      this.form.patchValue({
        rawScore: rawScore ?? null,
        scaledScore: score ?? null,
        percentileRank: percentileRank ?? null,
        level: levels?.find((lvl) => lvl.levelId === level)?.levelId ?? '',
      })
    }

    this.levels = levels ?? []
    this.templateId = templateId
    this.groupId = groupId
    this.itemId = itemId
    this.name = name
    this.description = description
    this.groupName = groupName

    const rawScoreLabel = this.isCoreLanguage
      ? 'Combined Scaled Score'
      : 'Raw Score'
    const scoreLabel = this.isCoreLanguage ? 'Standard Score' : 'Scaled Score'
    this.rawScoreLabel = this.commonService.translateSafe(rawScoreLabel)
    this.scoreLabel = this.commonService.translateSafe(scoreLabel)
  }
}
